<template>
  <div class="space-y-4">
    <!-- Edit warning: impacts users on this level -->
    <div v-if="level && level._id"
         class="p-3 rounded-md text-sm bg-amber-50 text-amber-800 border border-amber-200
         flex items-start gap-2">
      <i class="pi pi-exclamation-triangle mt-0.5 shrink-0" aria-hidden="true"></i>
      <span>Editing this level will impact users currently on this level.</span>
    </div>

    <!-- Basic Level Information -->
    <div class="grid grid-cols-2 gap-4">
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          Level Number *
        </label>
        <input
          v-model="levelData.level"
          type="number"
          min="1"
          class="w-full px-3 py-2 border border-gray-300 rounded-md
           focus:outline-none focus:ring-2 focus:ring-blue-500"
          :disabled="!!(level && level._id)"
        />
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          Title *
        </label>
        <input
          v-model="levelData.title"
          type="text"
          class="w-full px-3 py-2 border border-gray-300 rounded-md
           focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="e.g., Beginner, Novice, Expert"
        />
      </div>
    </div>

    <div>
      <label class="block text-sm font-medium text-gray-700 mb-1">
        Total Development Points *
      </label>
      <input
        :value="levelData.totalXP"
        type="number"
        min="1"
        class="w-full px-3 py-2 border border-gray-300 rounded-md
         focus:outline-none focus:ring-2 focus:ring-blue-500"
        placeholder="Total development points required for this level"
        disabled
      />

      <!-- Development points validation hints -->
      <div v-if="totalXpValidation" class="mt-2 p-2 rounded-md text-sm"
           :class="{
             'bg-yellow-100 text-yellow-800': totalXpValidation.status === 'warning',
             'bg-red-100 text-red-800': totalXpValidation.status === 'error',
             'bg-green-100 text-green-800': totalXpValidation.status === 'success'
           }">
        <i :class="{
          'pi pi-exclamation-triangle': totalXpValidation.status === 'warning',
          'pi pi-exclamation-circle': totalXpValidation.status === 'error',
          'pi pi-check-circle': totalXpValidation.status === 'success'
        }" class="mr-1"></i>
        {{ totalXpValidation.message }}
      </div>
    </div>

    <div>
      <label class="block text-sm font-medium text-gray-700 mb-1">
        Estimated finish date (target date for this level)
      </label>
      <input
        v-model="levelData.estimatedFinishDate"
        type="date"
        class="w-full px-3 py-2 border border-gray-300 rounded-md
         focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <p class="text-sm text-gray-500 mt-1">
        Optional. Used to compute delay vs target in user history and exports.
      </p>
    </div>

    <div>
      <label class="flex items-center gap-2">
        <input
          v-model="levelData.isStabilisationLevel"
          type="checkbox"
          class="rounded border-gray-300"
        />
        <span class="text-sm font-medium text-gray-700">Stabilisation level</span>
      </label>
      <p v-if="levelData.isStabilisationLevel" class="text-sm text-amber-700 mt-1 ml-6">
        No development points will be awarded in this level. Regular activity is still tracked.
      </p>
    </div>

    <!-- Assign to All Users Checkbox -->
    <div>
      <label class="flex items-center">
        <input
          v-model="levelData.assignToAllUsers"
          type="checkbox"
          class="mr-2"
        />
        <span class="text-sm font-medium text-gray-700">
          Automatically assign this level to all existing users
        </span>
      </label>
      <p class="text-sm text-gray-600 mt-1 ml-6">
        When checked, this level will be automatically
         assigned to all users who don't already have it
      </p>
    </div>

    <!-- Goals Section -->
    <div>
      <div class="flex justify-between items-center mb-3">
        <label class="block text-sm font-medium text-gray-700">
          Goals *
        </label>
        <Button
          label="Add Goal"
          icon="pi pi-plus"
          size="small"
          @click="addGoal"
        />
      </div>

      <div v-if="levelData.goals.length === 0" class="text-gray-500 text-center py-4">
        No goals defined. Add at least one goal to continue.
      </div>

      <div v-else class="space-y-3">
        <div
          v-for="(goal, index) in levelData.goals"
          :key="index"
          class="border border-gray-200 rounded-lg p-4"
        >
          <div class="flex justify-between items-start mb-3">
            <h4 class="font-medium">Goal {{ index + 1 }}</h4>
            <Button
              icon="pi pi-trash"
              size="small"
              severity="danger"
              @click="removeGoal(index)"
            />
          </div>

          <div class="grid grid-cols-2 gap-4 mb-3">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Title (EN) *
              </label>
              <input
                v-model="goal.title.en"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded-md
                 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Complete Assessment"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Title (DE) *
              </label>
              <input
                v-model="goal.title.de"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded-md
                 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="z.B., Bewertung abschließen"
              />
            </div>
          </div>
          <div class="grid grid-cols-2 gap-4 mb-3">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Development Points *
                </label>
                <input
                  v-model="goal.xp"
                  type="number"
                  min="1"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md"
                  :class="goal.type === 'assessment' ? 'bg-gray-100 cursor-not-allowed' :
                   'focus:outline-none focus:ring-2 focus:ring-blue-500'"
                  placeholder="Development points reward"
                  :disabled="goal.type === 'assessment'"
                  :title="goal.type === 'assessment'
              ? 'Development points are automatically set from assessmentschema settings'
                   : ''"
                />
                <p v-if="goal.type === 'assessment'" class="text-xs text-gray-500 mt-1">
                  Development points are set automatically from assessment schema
                </p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Required Completions *
              </label>
              <input
                v-model="goal.requiredCompletions"
                type="number"
                min="1"
                class="w-full px-3 py-2 border border-gray-300 rounded-md
                 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 1"
              />
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4 mb-3">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Image
              </label>
              <div class="flex items-center gap-3">
                <img
                  v-if="goal.img"
                  :src="goal.img"
                  alt="Goal image"
                  class="w-10 h-10 rounded object-cover border border-gray-200"
                  @error="(e) => e.target.style.display = 'none'"
                />
                <div
                  v-else
                  class="w-10 h-10 rounded bg-gray-100 flex items-center justify-center border border-gray-200"
                >
                  <i class="pi pi-image text-gray-400"></i>
                </div>
                <Button
                  label="Choose"
                  icon="pi pi-images"
                  size="small"
                  severity="secondary"
                  @click="openImagePicker(index)"
                />
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Type *
              </label>
              <select
                v-model="goal.type"
                class="w-full px-3 py-2 border border-gray-300 rounded-md
                 focus:outline-none focus:ring-2 focus:ring-blue-500"
                @change="onGoalTypeChange(goal)"
              >
                <option value="">Select type</option>
                <option
                  v-for="entry in registryEntries"
                  :key="entry.type"
                  :value="entry.type"
                >
                  {{ entry.label }}
                </option>
              </select>
            </div>
          </div>

          <!-- Assessment-specific fields -->
          <div v-if="goal.type === 'assessment'" class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Assessment Type *
              </label>
              <select
                v-model="goal.assessmentType"
                class="w-full px-3 py-2 border border-gray-300 rounded-md
                 focus:outline-none focus:ring-2 focus:ring-blue-500"
                @change="onAssessmentTypeChange(goal)"
              >
                <option value="">Select assessment type</option>
                <option
                  v-for="schema in enabledAssessmentSchemas"
                  :key="schema.type"
                  :value="schema.type"
                >
                  {{ schema.type }}
                   ({{ schema?.config?.levelSystem?.xpPerCompletion ?? 0 }} Development points)
                </option>
              </select>
            </div>
          </div>

          <div class="mt-3">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Estimated finish date (target date for this goal)
            </label>
            <input
              v-model="goal.estimatedFinishDate"
              type="date"
              class="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md
               focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="flex justify-end space-x-3 pt-4 border-t">
      <Button
        label="Cancel"
        severity="secondary"
        @click="$emit('cancel')"
      />
      <Button
        label="Save Level"
        icon="pi pi-check"
        @click="saveLevel"
        :disabled="!isValid"
      />
    </div>

    <ImagePickerDialog
      v-model="activeGoalImg"
      v-model:visible="imagePickerVisible"
    />
  </div>
</template>

<script>
import Button from 'primevue/button';
import ImagePickerDialog from './ImagePickerDialog.vue';

export default {
  name: 'LevelDialog',
  components: {
    Button,
    ImagePickerDialog,
  },
  props: {
    level: {
      type: Object,
      default: null,
      validator: (value) => value === null || (typeof value === 'object' && value !== null),
    },
    existingLevels: {
      type: Array,
      default: () => [],
      validator: (value) => Array.isArray(value),
    },
    registryEntries: {
      type: Array,
      default: () => [],
      validator: (value) => Array.isArray(value),
    },
    assessmentSchemas: {
      type: Array,
      default: () => [],
      validator: (value) => Array.isArray(value),
    },
  },
  data() {
    return {
      levelData: {
        level: 1,
        title: '',
        totalXP: 0,
        estimatedFinishDate: null,
        goals: [],
      },
      imagePickerVisible: false,
      imagePickerGoalIndex: -1,
    };
  },
  computed: {
    nextLevelNumber() {
      if (!this.existingLevels || this.existingLevels.length === 0) {
        return 1;
      }
      const maxLevel = Math
        .max(...this.existingLevels.map((l) => (l.level != null ? Number(l.level) : 0)));
      return maxLevel + 1;
    },
    enabledAssessmentSchemas() {
      console.log('LevelDialog - received assessmentSchemas:', this.assessmentSchemas);

      if (!this.assessmentSchemas || !Array.isArray(this.assessmentSchemas)) {
        console.log('LevelDialog - assessmentSchemas is not an array:', this.assessmentSchemas);
        return [];
      }

      const enabled = this.assessmentSchemas.filter((schema) => {
        const levelSystemEnabled = schema?.config?.levelSystem?.enabled ?? false;
        const xpPerCompletion = schema?.config?.levelSystem?.xpPerCompletion ?? 0;

        console.log('LevelDialog - checking schema:', {
          type: schema.type,
          levelSystemEnabled,
          xpPerCompletion,
          levelSystemEnabledType: typeof levelSystemEnabled,
          xpPerCompletionType: typeof xpPerCompletion,
        });

        const isEnabled = levelSystemEnabled && xpPerCompletion > 0;
        console.log('LevelDialog - schema enabled?', isEnabled);
        return isEnabled;
      });

      console.log('LevelDialog - enabled schemas:', enabled);
      return enabled;
    },

    goalXpSum() {
      if (!this.levelData.goals || this.levelData.goals.length === 0) {
        return 0;
      }
      return this.levelData.goals
        .reduce((sum, goal) => sum + (goal.xp * goal.requiredCompletions || 0), 0);
    },

    totalXpValidation() {
      if (!this.levelData.goals || this.levelData.goals.length === 0) {
        return null;
      }

      if (this.goalXpSum === 0) {
        return null; // No goals yet
      }

      if (this.goalXpSum < this.levelData.totalXP) {
        return {
          status: 'warning',
          message: `Goals development points sum (${this.goalXpSum}) is less than level total development points (${this.levelData.totalXP}). Add more goals or reduce total development points.`,
        };
      }

      if (this.goalXpSum > this.levelData.totalXP) {
        return {
          status: 'error',
          message: `Goals development points sum (${this.goalXpSum}) exceeds level total development points (${this.levelData.totalXP}). Reduce goal development points values or increase total development points.`,
        };
      }

      return {
        status: 'success',
        message: `Goals development points sum (${this.goalXpSum}) matches level total development points (${this.levelData.totalXP}). Perfect!`,
      };
    },

    activeGoalImg: {
      get() {
        const goal = this.levelData.goals?.[this.imagePickerGoalIndex];
        return goal?.img || '';
      },
      set(url) {
        if (this.imagePickerGoalIndex >= 0 && this.levelData.goals?.[this.imagePickerGoalIndex]) {
          this.levelData.goals[this.imagePickerGoalIndex].img = url;
        }
      },
    },

    isValid() {
      return (
        this.levelData.level > 0 &&
        this.levelData.title && this.levelData.title.trim() !== '' &&
        this.levelData.totalXP > 0 &&
        this.levelData.goals && Array.isArray(this.levelData.goals)
         && this.levelData.goals.length > 0 &&
        this.levelData.goals.every((goal) => this.isGoalValid(goal)) &&
        (!this.totalXpValidation || this.totalXpValidation.status !== 'error')
      );
    },
  },
  watch: {
    goalXpSum(newValue) {
      this.levelData.totalXP = newValue;
    },
    level: {
      handler(newLevel) {
        if (newLevel && typeof newLevel === 'object') {
          this.levelData = { ...newLevel };
          this.levelData.isStabilisationLevel = this.levelData.isStabilisationLevel ?? false;

          // Migrate old goal structure to new multilingual structure
          if (this.levelData.goals && Array.isArray(this.levelData.goals)) {
            this.levelData.goals = this.levelData.goals.map((goal) => {
            // If goal has old title structure (string), migrate to new structure
              if (typeof goal.title === 'string') {
                return {
                  ...goal,
                  title: {
                    en: goal.title,
                    de: '',
                  },
                  estimatedFinishDate: goal.estimatedFinishDate ?? null,
                };
              }
              // If goal already has new structure, ensure both en and de exist
              if (goal.title && typeof goal.title === 'object') {
                return {
                  ...goal,
                  title: {
                    en: goal.title.en || '',
                    de: goal.title.de || '',
                  },
                  estimatedFinishDate: goal.estimatedFinishDate ?? null,
                };
              }
              // If no title exists, initialize with empty structure
              return {
                ...goal,
                title: {
                  en: '',
                  de: '',
                },
                estimatedFinishDate: goal.estimatedFinishDate ?? null,
              };
            });
          }
        } else {
          this.resetForm();
          this.levelData.level = this.nextLevelNumber;
        }
      },
      immediate: true,
    },
    assessmentSchemas: {
      handler(newSchemas) {
        console.log('LevelDialog - assessmentSchemas prop changed:', newSchemas);
        console.log('LevelDialog - prop type:', typeof newSchemas);
        console.log('LevelDialog - prop is array:', Array.isArray(newSchemas));
        if (newSchemas && Array.isArray(newSchemas)) {
          console.log('LevelDialog - first schema:', newSchemas[0]);
        }
      },
      immediate: true,
    },

    // Watch for assessmentType changes to auto-update XP
    'levelData.goals': {
      handler(newGoals) {
        if (newGoals && Array.isArray(newGoals)) {
          newGoals.forEach((goal, index) => {
            if (goal.type === 'assessment' && goal.assessmentType) {
              const schema = this.enabledAssessmentSchemas
                .find((s) => s.type === goal.assessmentType);
              const xpPerCompletion = schema?.config?.levelSystem?.xpPerCompletion ?? 0;
              if (schema && xpPerCompletion > 0 && goal.xp !== xpPerCompletion) {
                // Update the goal in the array instead of modifying the parameter
                this.levelData.goals[index] = {
                  ...this.levelData.goals[index],
                  xp: xpPerCompletion,
                };
              }
            }
          });
        }
      },
      deep: true,
    },
  },
  methods: {
    openImagePicker(goalIndex) {
      this.imagePickerGoalIndex = goalIndex;
      this.imagePickerVisible = true;
    },

    resetForm() {
      this.levelData = {
        level: 1,
        title: '',
        totalXP: 0,
        estimatedFinishDate: null,
        isStabilisationLevel: false,
        goals: [],
        assignToAllUsers: false,
      };
    },

    addGoal() {
      // Ensure goals array exists
      if (!this.levelData.goals) {
        this.levelData.goals = [];
      }

      // Auto-generate a unique goal ID (length-based IDs collide when goals are removed and re-added)
      const goalId = `goal_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

      this.levelData.goals.push({
        id: goalId,
        title: {
          en: '',
          de: '',
        },
        img: 'default-icon.png', // Default image path
        xp: 1,
        type: '',
        assessmentType: '',
        requiredCompletions: 1,
        completed: false, // Default to false for new goals
        estimatedFinishDate: null,
      });
    },

    removeGoal(index) {
      // Ensure goals array exists and index is valid
      if (this.levelData.goals && Array.isArray(this.levelData.goals)
        && index >= 0 && index < this.levelData.goals.length) {
        this.levelData.goals.splice(index, 1);
      }
    },

    onGoalTypeChange(goal) {
      // Reset assessment-specific fields when type changes
      if (goal.type !== 'assessment' && this.levelData.goals && Array.isArray(this.levelData.goals)) {
        // Create a copy to avoid modifying the parameter directly
        const goalIndex = this.levelData.goals.findIndex((g) => g.id === goal.id);
        if (goalIndex !== -1) {
          this.levelData.goals[goalIndex] = {
            ...this.levelData.goals[goalIndex],
            assessmentType: '',
          };
        }
      }

      // Auto-set XP for assessment goals based on schema
      if (goal.type === 'assessment' && goal.assessmentType) {
        const schema = this.enabledAssessmentSchemas
          .find((s) => s.type === goal.assessmentType);
        const xpPerCompletion = schema?.config?.levelSystem?.xpPerCompletion ?? 0;
        if (schema && xpPerCompletion > 0) {
          // Update the goal in the array instead of modifying the parameter
          const goalIndex = this.levelData.goals.findIndex((g) => g.id === goal.id);
          if (goalIndex !== -1) {
            this.levelData.goals[goalIndex] = {
              ...this.levelData.goals[goalIndex],
              xp: xpPerCompletion,
            };
          }
        }
      }
    },

    onAssessmentTypeChange(goal) {
      // Auto-update XP when assessment type changes
      if (goal.type === 'assessment' && goal.assessmentType) {
        const schema = this.enabledAssessmentSchemas
          .find((s) => s.type === goal.assessmentType);
        const xpPerCompletion = schema?.config?.levelSystem?.xpPerCompletion ?? 0;
        if (schema && xpPerCompletion > 0) {
          // Update the goal in the array instead of modifying the parameter
          const goalIndex = this.levelData.goals.findIndex((g) => g.id === goal.id);
          if (goalIndex !== -1) {
            this.levelData.goals[goalIndex] = {
              ...this.levelData.goals[goalIndex],
              xp: xpPerCompletion,
            };
            console.log(`Auto-updated XP for assessment goal to ${xpPerCompletion} based on schema ${goal.assessmentType}`);

            // Force reactivity update
            this.$forceUpdate();
          }
        }
      }
    },

    isGoalValid(goal) {
      // Ensure all required properties exist and are valid
      if (!goal || typeof goal !== 'object') {
        return false;
      }

      const baseValid = (
        goal.title && goal.title.en && goal.title.en.trim() !== '' &&
        goal.title && goal.title.de && goal.title.de.trim() !== '' &&
        goal.img && goal.img.trim() !== '' && // Ensure img is provided
        goal.xp > 0 &&
        goal.type && goal.type.trim() !== '' &&
        goal.requiredCompletions > 0 &&
        typeof goal.completed === 'boolean' // Ensure completed is boolean
      );

      if (goal.type === 'assessment') {
        return baseValid && goal.assessmentType && goal.assessmentType.trim() !== '';
      }

      return baseValid;
    },

    saveLevel() {
      console.log('Saving level with data:', JSON.stringify(this.levelData, null, 2));
      console.log('Form validation status:', this.isValid);
      console.log('XP validation:', this.totalXpValidation);

      if (this.isValid) {
        // Ensure data types are correct before sending
        const dataToSave = {
          ...this.levelData,
          totalXP: parseInt(this.levelData.totalXP, 10) || 0,
          level: parseInt(this.levelData.level, 10) || 1,
          goals: this.levelData.goals.map((goal) => ({
            ...goal,
            xp: parseInt(goal.xp, 10) || 1,
            requiredCompletions: parseInt(goal.requiredCompletions, 10) || 1,
          })),
        };

        console.log('Data to save after type conversion:', JSON.stringify(dataToSave, null, 2));
        this.$emit('save', dataToSave);
      } else {
        // Optionally show validation error message
        console.warn('Form validation failed. Please check all required fields.');
      }
    },
  },
};
</script>

<style scoped>
/* Component-specific styles can be added here */
</style>
